import { Menu } from "antd";
import type { MenuProps } from "antd";
import { useState, useRef, useMemo } from "react";
import {
  AppstoreOutlined,
  FileTextOutlined,
  StarOutlined,
  NumberOutlined,
  LoadingOutlined,
  UnorderedListOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useSpacesQuery } from "./[orgId]/space/useSpacesQuery";
import { SpaceItemType } from "./api/[orgId]/spaces/types";
import { BookClosedIcon } from "src/components/BookClosedIcon";
import { ContentViews } from "./[orgId]/explore/lib/useContentView";
import { serializeDocument } from "./[orgId]/explore/lib/useExploreDocument";
import { serializeTabs } from "./[orgId]/explore/lib/useTabs";
import { serializeContentView } from "./[orgId]/explore/lib/useContentView";
import { theme } from "antd";
import {
  Item,
  ItemType,
  BoardItem,
  QueryItem,
  TopicItem,
  ReportItem,
} from "src/components/useLibrary";
import { useColorThemer } from "src/components/patterns/Background";
import { useApplicationContext } from "src/hooks/useApplication";
import { ItemTypeIcon } from "src/components/icons/ItemTypeIcon";

type MenuItem = Required<MenuProps>["items"][number] & {
  children?: MenuItem[];
};

interface Space {
  uuid: string;
  name: string;
  color?: string;
}

interface FavoritesMenuProps {
  orgId: string;
  pinnedSpaces: Space[];
  libraryItems: Item[];
  menuLabel?: {
    icon?: React.ReactNode;
    text: string;
  };
  disabled?: boolean;
}

export function FavoritesMenu({
  orgId,
  pinnedSpaces,
  libraryItems,
  menuLabel = { icon: <StarOutlined />, text: "Favorites" },
  disabled = false,
}: FavoritesMenuProps) {
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | undefined>(
    undefined
  );
  const menuRef = useRef<HTMLDivElement>(null);
  const { space, spaceQuery } = useSpacesQuery(selectedSpaceId);
  const { token } = theme.useToken();
  const colorizer = useColorThemer();
  const app = useApplicationContext();

  const validateMenuItem = <T extends { id?: string; name?: string }>(
    item: T
  ): item is T & { id: string; name: string } =>
    Boolean(item.id) && Boolean(item.name);

  const renderCategoryHeader = (
    icon: React.ReactNode,
    label: string
  ): MenuItem => ({
    key: `header-${label.toLowerCase()}`,
    type: "group",
    label: (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          color: token.colorTextTertiary,
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
        }}
      >
        {/* {icon} */}
        <span>{label}</span>
      </div>
    ),
    children: [],
  });

  const favoritedBoards = useMemo(
    () =>
      libraryItems
        .filter(
          (item) =>
            item.itemType === ItemType.BOARDS &&
            (item as BoardItem).isBookmarked
        )
        .filter(validateMenuItem)
        .map((item) => ({
          id: (item as BoardItem).id,
          name: item.name,
        })),
    [libraryItems]
  );

  const favoritedQueries = useMemo(
    () =>
      libraryItems
        .filter(
          (item) =>
            item.itemType === ItemType.QUERIES &&
            (item as QueryItem).isBookmarked
        )
        .filter(validateMenuItem)
        .map((item) => {
          const queryItem = item as QueryItem;
          return {
            id: queryItem.id,
            name: queryItem.name,
            serialized: queryItem.json.serialized,
            viz: queryItem.json.viz.at(0),
          };
        }),
    [libraryItems]
  );

  const favoritedTopics = useMemo(
    () =>
      libraryItems
        .filter(
          (item) =>
            item.itemType === ItemType.TOPICS &&
            (item as TopicItem).isBookmarked
        )
        .filter(validateMenuItem)
        .map((item) => ({
          id: (item as TopicItem).id,
          name: item.name,
        })),
    [libraryItems]
  );

  const favoritedReports = useMemo(
    () =>
      libraryItems
        .filter(
          (item) =>
            item.itemType === ItemType.REPORTS &&
            (item as ReportItem).isBookmarked
        )
        .filter(validateMenuItem)
        .map((item) => ({
          id: (item as ReportItem).id,
          name: item.name,
        })),
    [libraryItems]
  );

  const memoizedSpaceItems = useMemo(
    () =>
      pinnedSpaces.length > 0
        ? [
            renderCategoryHeader(
              <ItemTypeIcon type={ItemType.SPACES} />,
              "Spaces"
            ),
            ...pinnedSpaces.map((pinnedSpace) => ({
              key: `space-${pinnedSpace.uuid}`,
              label: (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      backgroundColor: colorizer(
                        pinnedSpace.color || token.colorPrimary
                      ),
                      opacity: 0.8,
                    }}
                  />
                  <span>{pinnedSpace.name}</span>
                  {spaceQuery.isLoading &&
                    pinnedSpace.uuid === selectedSpaceId && (
                      <LoadingOutlined
                        style={{ fontSize: 12, marginLeft: 4 }}
                      />
                    )}
                </div>
              ),
              children:
                space?.items && pinnedSpace.uuid === selectedSpaceId
                  ? space.items.map((item) => ({
                      key: `${pinnedSpace.uuid}-${item.uuid}`,
                      label: (
                        <div
                          onClick={() => {
                            app.appendTab(
                              item.name,
                              `/${orgId}/space/${pinnedSpace.uuid}/${item.uuid}/launch`,
                              { open: true }
                            );
                          }}
                          style={{ cursor: "pointer", color: "inherit" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {item.type === SpaceItemType.Board ? (
                              <ItemTypeIcon type={ItemType.BOARDS} />
                            ) : item.type === SpaceItemType.Entity ? (
                              <NumberOutlined />
                            ) : item.type === SpaceItemType.Query ? (
                              <ItemTypeIcon type={ItemType.QUERIES} />
                            ) : (
                              <ItemTypeIcon type={ItemType.SPACES} />
                            )}
                            <span>{item.name}</span>
                          </div>
                        </div>
                      ),
                    }))
                  : [],
            })),
          ]
        : [],
    [pinnedSpaces, selectedSpaceId, space, spaceQuery.isLoading, orgId, app]
  );

  const memoizedLibraryItems = useMemo(
    () => [
      ...(favoritedBoards.length > 0
        ? [
            renderCategoryHeader(<AppstoreOutlined />, "Boards"),
            ...favoritedBoards.map((board) => ({
              key: `board-${board.id}`,
              label: (
                <div
                  onClick={() => {
                    app.appendTab(board.name, `/${orgId}/boards/${board.id}`, {
                      open: true,
                    });
                  }}
                  style={{ cursor: "pointer", color: "inherit" }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <AppstoreOutlined />
                    <span>{board.name}</span>
                  </div>
                </div>
              ),
              className: "favorites-menu-item",
            })),
          ]
        : []),

      ...(favoritedQueries.length > 0
        ? [
            renderCategoryHeader(<FileTextOutlined />, "Queries"),
            ...favoritedQueries.map((query) => ({
              key: `query-${query.id}`,
              label: (
                <div
                  onClick={() => {
                    app.appendTab(
                      query.name,
                      `/${orgId}/explore?s=${
                        query.serialized
                      }&d=${serializeDocument({
                        saved: query.id,
                        title: query.name,
                      })}&t=${serializeTabs({})}&v=${serializeContentView({
                        value: query.viz?.value as ContentViews | undefined,
                        config: query.viz?.config,
                      })}`,
                      { open: true }
                    );
                  }}
                  style={{ cursor: "pointer", color: "inherit" }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <FileTextOutlined />
                    <span>{query.name}</span>
                  </div>
                </div>
              ),
            })),
          ]
        : []),

      ...(favoritedTopics.length > 0
        ? [
            renderCategoryHeader(<UnorderedListOutlined />, "Topics"),
            ...favoritedTopics.map((topic) => ({
              key: `topic-${topic.id}`,
              label: (
                <div
                  onClick={() => {
                    app.appendTab(topic.name, `/${orgId}/topics/${topic.id}`, {
                      open: true,
                    });
                  }}
                  style={{ cursor: "pointer", color: "inherit" }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <UnorderedListOutlined />
                    <span>{topic.name}</span>
                  </div>
                </div>
              ),
            })),
          ]
        : []),

      ...(favoritedReports.length > 0
        ? [
            renderCategoryHeader(<BookClosedIcon />, "Reports"),
            ...favoritedReports.map((report) => ({
              key: `report-${report.id}`,
              label: (
                <div
                  onClick={() => {
                    app.appendTab(
                      report.name,
                      `/${orgId}/reports/${report.id}`,
                      { open: true }
                    );
                  }}
                  style={{ cursor: "pointer", color: "inherit" }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <BookClosedIcon />
                    <span>{report.name}</span>
                  </div>
                </div>
              ),
            })),
          ]
        : []),
    ],
    [
      favoritedBoards,
      favoritedQueries,
      favoritedTopics,
      favoritedReports,
      orgId,
      app,
    ]
  );

  const rootItems = useMemo(
    () => [
      {
        key: "favorites",
        label: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "inherit",
              width: "100%",
              justifyContent: app.sidebar.isOpen ? "flex-start" : "center",
              opacity: disabled ? 0.3 : 1,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 16,
                height: 16,
                opacity: 0.6,
              }}
            >
              {menuLabel.icon}
            </div>
            {app.sidebar.isOpen && (
              <span style={{ flex: 1 }}>{menuLabel.text}</span>
            )}
          </div>
        ),
        children: disabled
          ? []
          : [...memoizedSpaceItems, ...memoizedLibraryItems].filter(Boolean),
      },
    ],
    [
      menuLabel,
      memoizedSpaceItems,
      memoizedLibraryItems,
      app.sidebar.isOpen,
      disabled,
    ]
  );

  return (
    <div ref={menuRef}>
      <Menu
        mode="vertical"
        items={rootItems}
        expandIcon={<RightOutlined />}
        onOpenChange={(openKeys) => {
          if (disabled) return;
          const spaceKey = openKeys.find((key) => key.startsWith("space-"));
          if (spaceKey) {
            setSelectedSpaceId(spaceKey.replace("space-", ""));
          }
        }}
        style={{
          border: "none",
          backgroundColor: "transparent",
          minWidth: app.sidebar.isOpen ? "200px" : "32px",
          color: "inherit",
          fontSize: "12px",
          padding: 0,
          margin: 0,
          opacity: disabled ? 0.3 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
        className="favorites-menu"
      />
    </div>
  );
}
